import { type ColumnDef } from "@tanstack/react-table";
import { Building2, Briefcase, Eye, Plus, Check, X } from "lucide-react";
import { type NavigateFunction } from "react-router-dom";
import { routerPaths } from "@/ui/router";
import { InfoTooltip } from "@/ui/atoms";
import type { TBusinessPartnerType } from "@/store";
import { STRING_MULTI_SELECT_BLANK } from "@/utils/tableMultiSelectFilter";
import type { TDealCountsSummary, TServiceProviderView } from "./createViewServiceProviders";

/**
 * Column definitions for the business-partners (service providers / portfolio
 * companies) table. Extracted from ViewServiceProviders to keep the view lean.
 */
export function createServiceProviderColumns(
    navigateToDealCreate: (bp: TServiceProviderView) => void,
    navigate: NavigateFunction,
    dealCounts: Record<string, TDealCountsSummary>,
    businessPartnerTypeLabels: Record<TBusinessPartnerType, string>,
    businessPartnerTypes: { value: TBusinessPartnerType; label: string }[],
    canCreateDeal: boolean = false,
    canViewDeals: boolean = false,
    businessPartnerOptions: { value: string; label: string }[] = [],
    employeeOptions: { value: string; label: string }[] = [],
): ColumnDef<TServiceProviderView>[] {
    // "(Blanks)" is always offered: a business partner with no employees is a
    // real state the column renders as "None", so it must be filterable.
    const employeeFilterOptions = [
        ...employeeOptions,
        { value: STRING_MULTI_SELECT_BLANK, label: "(Blanks)" },
    ];
    return [
        {
            accessorKey: "name",
            header: "Name",
            enableSorting: true,
            cell: ({ row }) => {
                const sp = row.original;
                return (
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <Building2 className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="font-medium">{sp.name}</p>
                            {sp.isTest && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                    Test
                                </span>
                            )}
                        </div>
                    </div>
                );
            },
            // Server filters by business_partner_ids; the ticked option values are BP ids.
            meta: {
                filterType: "multiSelect",
                filterOptions: businessPartnerOptions,
                filterSearchable: true,
            },
        },
        {
            accessorKey: "businessPartnerType",
            header: "Type",
            enableSorting: true,
            cell: ({ row }) => (
                <span className="text-sm text-muted-foreground">
                    {businessPartnerTypeLabels[row.original.businessPartnerType]}
                </span>
            ),
            meta: {
                filterType: "select",
                filterOptions: businessPartnerTypes,
                filterPlaceholder: "All types",
            },
            filterFn: (row, _columnId, filterValue) => {
                if (!filterValue) return true;
                return row.original.businessPartnerType === filterValue;
            },
        },
        {
            accessorKey: "canUploadSupportingFiles",
            header: "Upload Supporting Files",
            cell: ({ row }) => {
                const sp = row.original;
                return (
                    <div
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                            sp.canUploadSupportingFiles
                                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                    >
                        {sp.canUploadSupportingFiles ? (
                            <>
                                <Check className="h-3 w-3" />
                            </>
                        ) : (
                            <>
                                <X className="h-3 w-3" />
                            </>
                        )}
                    </div>
                );
            },
            meta: {
                filterType: "boolean",
                filterPlaceholder: "All statuses",
            },
            filterFn: (row, _columnId, filterValue) => {
                if (filterValue === undefined) return true;
                return row.original.canUploadSupportingFiles === filterValue;
            },
        },
        {
            accessorKey: "isActive",
            header: "Active",
            enableSorting: true,
            cell: ({ row }) => {
                const sp = row.original;
                return (
                    <div
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                            sp.isActive
                                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                    >
                        {sp.isActive ? (
                            <>
                                <Check className="h-3 w-3" />
                            </>
                        ) : (
                            <>
                                <X className="h-3 w-3" />
                            </>
                        )}
                    </div>
                );
            },
            meta: {
                filterType: "boolean",
                filterPlaceholder: "All statuses",
            },
            filterFn: (row, _columnId, filterValue) => {
                if (filterValue === undefined) return true;
                return row.original.isActive === filterValue;
            },
        },
        {
            accessorKey: "employees",
            header: "Employees",
            enableSorting: false,
            cell: ({ row }) => {
                const sp = row.original;
                if (!sp.employees || sp.employees.length === 0) {
                    return <span className="text-xs text-muted-foreground italic">None</span>;
                }

                // Cap the pills shown so a BP with many employees doesn't blow up
                // the row height; the rest collapse into a "+N more" pill whose
                // tooltip lists the hidden names. Full list lives on the details page.
                const MAX_VISIBLE_EMPLOYEES = 3;
                const visibleEmployees = sp.employees.slice(0, MAX_VISIBLE_EMPLOYEES);
                const hiddenEmployees = sp.employees.slice(MAX_VISIBLE_EMPLOYEES);

                return (
                    <div className="flex flex-wrap gap-1">
                        {visibleEmployees.map((employee) => (
                            <span
                                key={employee.id}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700"
                                title={employee.email}
                            >
                                {employee.display_name}
                            </span>
                        ))}
                        {hiddenEmployees.length > 0 && (
                            <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600"
                                title={hiddenEmployees.map((e) => e.display_name).join(", ")}
                            >
                                +{hiddenEmployees.length} more
                            </span>
                        )}
                    </div>
                );
            },
            // Server filters by employee_ids (+ employees_unassigned for "(Blanks)");
            // the ticked option values are user ids. A partner matches if ANY of
            // its employees is ticked, so the pill cap above never hides a match.
            meta: {
                filterType: "multiSelect",
                filterOptions: employeeFilterOptions,
                filterSearchable: true,
            },
        },
        {
            id: "deals",
            header: "",
            cell: ({ row }) => {
                const sp = row.original;
                const counts = dealCounts[sp.id];
                const total = counts?.total ?? 0;
                const active = counts?.active ?? 0;
                // No deal affordances for subgroups that can neither view the deals
                // list (deal_view) nor create deals. Business Partners hold deal_view,
                // so they see the "Deals" button and reach their own BP's deals list.
                if (!canViewDeals && !canCreateDeal) {
                    return null;
                }
                return (
                    <div className="relative inline-flex rounded-md shadow-sm">
                        {/* Hidden at zero, which also covers the render before the
                            counts have loaded. The badge is focusable so the tooltip
                            explaining the two numbers is reachable by keyboard. */}
                        {total > 0 && (
                            <InfoTooltip
                                content={
                                    <>
                                        Deals on this business partner: {active} active of {total}{" "}
                                        total. Inactive deals keep their files, mappings and history
                                        but cannot be selected when uploading.
                                    </>
                                }
                            >
                                <span
                                    tabIndex={0}
                                    aria-label={`${active} of ${total} deals active`}
                                    className="absolute -top-2 -right-2 inline-flex cursor-default items-center justify-center min-w-[26px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold z-10 focus:outline-none focus:ring-2 focus:ring-red-300"
                                >
                                    {active}/{total}
                                </span>
                            </InfoTooltip>
                        )}
                        {canCreateDeal && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigateToDealCreate(sp);
                                }}
                                className="inline-flex items-center px-2 py-1.5 rounded-l-md text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors border-r border-emerald-200"
                                title="Create New Deal"
                            >
                                <Plus className="h-3.5 w-3.5" />
                            </button>
                        )}
                        {canViewDeals && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(routerPaths.admin.businessPartnerDeals(sp.id));
                                }}
                                className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors ${canCreateDeal ? "rounded-r-md" : "rounded-md"}`}
                                title="View Deals"
                            >
                                <Briefcase className="h-3.5 w-3.5" />
                                Deals
                            </button>
                        )}
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            id: "details",
            header: "",
            cell: ({ row }) => {
                const sp = row.original;
                return (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(routerPaths.admin.businessPartnerDetails(sp.id));
                        }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        title="Details"
                    >
                        <Eye className="h-3.5 w-3.5" />
                        Details
                    </button>
                );
            },
            enableSorting: false,
        },
    ];
}
